import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { ConditionBadge } from '@siliconx/shared/ui/ConditionBadge';
import { LifecycleBadge } from '@siliconx/shared/ui/LifecycleBadge';
import { UsdCell } from '@siliconx/shared/ui/UsdCell';
import { DataTable } from '@siliconx/shared/ui/DataTable';
import { FmvHistoryLine, TelemetryScoreHistoryLine } from '@siliconx/shared/ui/FmvLine';
import { TelemetrySparkline } from '@siliconx/shared/ui/TelemetrySparkline';
import { TelemetryScoreCallout } from '@siliconx/shared/ui/TelemetryScoreCallout';
import { LoadingShell } from '@siliconx/shared/ui/state';
import { formatDate } from '../../lib/format';
import { ManufacturerLogo } from '../shared/ManufacturerLogo';
import { specRows } from '../../lib/specs';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import type {
  ClientTelemetryDay,
  TelemetryScoreClientResponse,
  TelemetryScoreHistoryClientResponse,
} from '@siliconx/shared';
import { MaskedSerial } from './MaskedSerial';
import type { AssetDetail, ApiError, ClientFmvSnapshot } from './asset-detail-types';

/**
 * SIL-565: the `?v=1` legacy asset-detail sections — split out of the route
 * file (routes/assets/$assetId.tsx) so it isn't the largest file in the app.
 * Acceptance criteria for the v2 tab split explicitly allows the legacy
 * branch to stay untabbed; this module is exactly that untabbed PanelTitle
 * stack, unchanged in content or behavior.
 */

export function SummarySection({ asset }: { asset: AssetDetail }) {
  return (
    <Card>
      <PanelTitle title="Asset summary" />
      <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Serial</dt>
          <dd className="mt-1 text-sm text-foreground"><MaskedSerial assetId={asset.id} hasSerial={asset.hasSerial} /></dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Asset class
          </dt>
          <dd className="mt-1 text-sm text-foreground">
            {asset.assetClassName ?? asset.assetClassId}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Condition</dt>
          <dd className="mt-1">
            <ConditionBadge value={asset.condition} />
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Lifecycle state
          </dt>
          <dd className="mt-1">
            {asset.lifecycleStateId ? (
              <LifecycleBadge id={asset.lifecycleStateId} label={asset.lifecycleStateName ?? asset.lifecycleStateId} />
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Created</dt>
          <dd className="mt-1 text-sm text-foreground">{formatDate(asset.createdAt)}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Updated</dt>
          <dd className="mt-1 text-sm text-foreground">{formatDate(asset.updatedAt)}</dd>
        </div>
      </dl>
    </Card>
  );
}

export function SkuSection({ asset }: { asset: AssetDetail }) {
  const hasSkuData =
    asset.manufacturerName || asset.modelName || asset.generation || asset.subClass || asset.shortDescription;

  if (!hasSkuData) return null;

  return (
    <Card>
      <PanelTitle title="SKU / manufacturer" />
      <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
        {asset.manufacturerName && (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Manufacturer
            </dt>
            <dd className="mt-1 text-sm text-foreground">
              <ManufacturerLogo id={asset.manufacturerId} name={asset.manufacturerName} size={16} />
            </dd>
          </div>
        )}
        {asset.modelName && (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Model</dt>
            <dd className="mt-1 text-sm text-foreground">{asset.modelName}</dd>
          </div>
        )}
        {asset.generation && (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Generation
            </dt>
            <dd className="mt-1 text-sm text-foreground">{asset.generation}</dd>
          </div>
        )}
        {asset.subClass && (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Sub-class
            </dt>
            <dd className="mt-1 text-sm text-foreground">{asset.subClass}</dd>
          </div>
        )}
        {asset.shortDescription && (
          <div className="col-span-2 sm:col-span-3">
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Description
            </dt>
            <dd className="mt-1 text-sm text-foreground">{asset.shortDescription}</dd>
          </div>
        )}
      </dl>
    </Card>
  );
}

export function SpecsSection({ asset }: { asset: AssetDetail }) {
  const rows = specRows(asset.assetClassId, asset.specFacts, asset.specDisplaySchema);

  return (
    <Card>
      <PanelTitle title="Specs" />
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">No spec data available.</p>
      ) : (
        <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
          {rows.map((r) => (
            <div key={r.key ?? r.label}>
              {r.label && (
                <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {r.label}
                </dt>
              )}
              <dd className="mt-1 text-sm text-foreground">{r.value ?? '—'}</dd>
            </div>
          ))}
        </dl>
      )}
    </Card>
  );
}

/**
 * Valuation & health hero — the top-of-page module a seller cares about most:
 * this unit's FMV median, its telemetry health score, and a "List for sale"
 * call-to-action. Mirrors the ops "Latest pricing" module (which shows the
 * same FMV + health call-out at a glance) but repackaged as a prominent,
 * on-brand hero for the customer portal.
 *
 * Data surface is the client projection only: FMV median + condition + the
 * telemetry health call-out (whose "Confidence N / 100" is telemetry coverage
 * — the seller-facing lever that rises as more of the unit's telemetry
 * reports). Low/high, net recovery and FMV-pricing confidence stay ops-only.
 *
 * The score query (GET /api/client/assets/:id/telemetry-score) is lifted to
 * the page and passed in so the relocated score-trend card can reuse it; 404
 * = no score yet, rendered as a quiet empty state, not an error.
 */
export function ValuationHero({
  fmv,
  score,
  allowsListing,
  inLot,
}: {
  fmv: { medUsd: number; pricedAt: string; conditionBand: string } | null;
  score: TelemetryScoreClientResponse | null;
  allowsListing: boolean;
  inLot: boolean;
}) {
  // Nudge while coverage hasn't reached the high tier (>80%). Coverage is what
  // the callout surfaces as "Confidence", and it climbs as more of the unit's
  // telemetry reports — so this is the honest, seller-actionable lever.
  const showNudge = score != null && score.coverage <= 0.8;

  function scrollToList() {
    document
      .getElementById('list-for-sale')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="rounded-xl border border-brand-200 bg-card p-6 shadow-sm ring-1 ring-brand-100">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Fair market value
          </div>
          {fmv ? (
            <>
              <div className="mt-1 flex flex-wrap items-baseline gap-3">
                <UsdCell value={fmv.medUsd} className="text-3xl font-semibold text-foreground" />
                <ConditionBadge value={fmv.conditionBand} />
              </div>
              <div className="mt-1 text-xs text-muted-foreground">Priced {formatDate(fmv.pricedAt)}</div>
            </>
          ) : (
            <div className="mt-1 text-sm text-muted-foreground">
              Valuation pending — no FMV snapshot yet.
            </div>
          )}
        </div>
        {allowsListing && !inLot && (
          <Button className="shrink-0"
            onClick={scrollToList}
          >
            List for sale
          </Button>
        )}
      </div>

      {score ? (
        <>
          <TelemetryScoreCallout
            className="mt-5 border-t border-border pt-5"
            overall={score.overall}
            coverage={score.coverage}
            scoredAt={score.scoredAt ?? ''}
            categories={score.categories}
          />
          {showNudge && (
            <p className="mt-3 text-xs text-muted-foreground">
              Confidence climbs as more of this unit&rsquo;s telemetry reports.
            </p>
          )}
        </>
      ) : (
        <div className="mt-5 border-t border-border pt-5 text-sm text-muted-foreground">
          Telemetry health not yet scored for this unit.
        </div>
      )}
    </div>
  );
}

const historyColumns: ColumnDef<ClientFmvSnapshot, unknown>[] = [
  {
    accessorKey: 'pricedAt',
    header: 'Date',
    cell: ({ getValue }) => (
      <span className="text-sm text-foreground">{formatDate(getValue() as string)}</span>
    ),
  },
  {
    accessorKey: 'fmvMedUsd',
    header: 'FMV Median',
    cell: ({ row, getValue }) => (
      <span className="inline-flex items-center gap-1.5">
        <UsdCell value={getValue() as number} />
        {/* SIL-1828 (R2): server flag, label only. */}
        {row.original.isDirectionalBaseline && (
          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
            Directional
          </span>
        )}
      </span>
    ),
  },
];

// Smooth-scroll anchor link pairing each chart with its data table below.
function JumpToTableLink({ targetId }: { targetId: string }) {
  return (
    <button
      type="button"
      onClick={() =>
        document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      className="text-xs font-medium text-blue-600 hover:text-blue-800"
    >
      Jump to data table ↓
    </button>
  );
}

// Scroll-contained table body (~30 rows per table) with a sticky header.
const TABLE_SCROLL_CLS = 'max-h-96 overflow-y-auto [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10';

export function FmvHistoryChartCard({ history }: { history: ClientFmvSnapshot[] }) {
  return (
    <Card>
      <div className="flex items-center justify-between">
        <PanelTitle title="FMV history" />
        {history.length > 0 && <JumpToTableLink targetId="fmv-snapshots-table" />}
      </div>
      {history.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">No FMV history yet.</p>
      ) : (
        <div className="mt-4">
          <FmvHistoryLine data={history} />
        </div>
      )}
    </Card>
  );
}

export function FmvHistoryTableCard({ history }: { history: ClientFmvSnapshot[] }) {
  if (history.length === 0) return null;
  return (
    <Card className="scroll-mt-6" id="fmv-snapshots-table">
      <PanelTitle title="FMV snapshots" />
      <div className="mt-4">
        <DataTable
          data={history}
          columns={historyColumns}
          emptyMessage="No FMV history yet."
          className={TABLE_SCROLL_CLS}
        />
      </div>
    </Card>
  );
}

const telemetryColumns: ColumnDef<ClientTelemetryDay, unknown>[] = [
  {
    accessorKey: 'day',
    header: 'Date',
    cell: ({ getValue }) => (
      <span className="text-sm text-foreground">{formatDate(getValue() as string)}</span>
    ),
  },
  {
    accessorKey: 'avgGpuTempC',
    header: 'GPU °C (avg)',
    cell: ({ getValue }) => {
      const v = getValue() as number | null;
      return <span className="text-sm text-foreground">{v != null ? `${v.toFixed(1)}°C` : '—'}</span>;
    },
  },
  {
    accessorKey: 'maxGpuTempC',
    header: 'GPU °C (max)',
    cell: ({ getValue }) => {
      const v = getValue() as number | null;
      return <span className="text-sm text-foreground">{v != null ? `${v.toFixed(1)}°C` : '—'}</span>;
    },
  },
  {
    accessorKey: 'avgGpuUtilPct',
    header: 'GPU %',
    cell: ({ getValue }) => {
      const v = getValue() as number | null;
      return <span className="text-sm text-foreground">{v != null ? `${v.toFixed(1)}%` : '—'}</span>;
    },
  },
  {
    accessorKey: 'avgPowerDrawWatts',
    header: 'Power (W)',
    cell: ({ getValue }) => {
      const v = getValue() as number | null;
      return <span className="text-sm text-foreground">{v != null ? `${v.toFixed(0)} W` : '—'}</span>;
    },
  },
  {
    accessorKey: 'derivedConditionBand',
    header: 'Health',
    cell: ({ getValue }) => <ConditionBadge value={getValue() as string | null} />,
  },
];

export function TelemetryChartCard({ rows }: { rows: ClientTelemetryDay[] }) {
  return (
    <Card>
      <div className="flex items-center justify-between">
        <PanelTitle title="Device telemetry (last 30 days)" />
        {rows.length > 0 && <JumpToTableLink targetId="telemetry-table" />}
      </div>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Daily telemetry aggregates are still being computed.
        </p>
      ) : (
        <div className="mt-4">
          <TelemetrySparkline data={rows} />
        </div>
      )}
    </Card>
  );
}

export function TelemetryTableCard({ rows }: { rows: ClientTelemetryDay[] }) {
  if (rows.length === 0) return null;
  return (
    <Card className="scroll-mt-6" id="telemetry-table">
      <PanelTitle title="Telemetry data (daily)" />
      <div className="mt-4">
        <DataTable
          data={[...rows].reverse()}
          columns={telemetryColumns}
          emptyMessage="Daily telemetry aggregates are still being computed."
          className={TABLE_SCROLL_CLS}
        />
      </div>
    </Card>
  );
}

// The latest telemetry health score now lives in the top-of-page ValuationHero
// call-out. This standalone card is the RUF-518 score trend, relocated to sit
// alongside FMV history; rendered by the page only when the asset has a score.
export function HealthScoreTrendCard({
  assetId,
  categories,
}: {
  assetId: string;
  categories: string[];
}) {
  const { data, isLoading } = useQuery<TelemetryScoreHistoryClientResponse, ApiError>({
    queryKey: qk.assets.telemetryScoreHistory(assetId),
    queryFn: async () => {
      const res = await api.api.client.assets[':assetId']['telemetry-score'].history.$get({
        param: { assetId },
      });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as TelemetryScoreHistoryClientResponse;
    },
  });

  return (
    <Card>
      <PanelTitle title="Health score trend" />
      {isLoading ? (
        <LoadingShell rows={2} />
      ) : (
        <div className="mt-4">
          <TelemetryScoreHistoryLine data={data?.data ?? []} categories={categories} />
        </div>
      )}
    </Card>
  );
}
